// MunosScriptHost — the supervisor in the supervisor/tenant model
// (docs/multi-script-client.md). It owns the one emulated game and runs
// any number of Munos scripts (ScriptTenants) side by side over it:
//
//   - the EmulatorCore, the ROM, and the composite framebuffer
//   - the wall-clock frame loop (one core step per frame, shared)
//   - the at-OAM-DMA frame-event gate (fires every tenant)
//   - ROM pause as a refcount across tenants (any-pauses-wins) plus a
//     separate devtools pause
//   - gamepad input fan-out (every tenant's latch sees each edge; the
//     ROM sees an edge only if no tenant consumed it)
//   - render compositing (each tenant paints in load order)
//   - audio drain
//
// It implements the devtools HostView: shared-game introspection
// (PPU / RAM / transport / savestate) delegates to the core; the
// script-overlay methods delegate to the authored tenant.
//
// This is the emulator-host half of what used to be MunosClient +
// MunosEmulatorHost fused by inheritance; the per-script half is
// ScriptTenant (script-tenant.ts).

import {
  ScriptTenant,
  LOCAL_BUS_KEY,
  type TenantContext,
  type TenantSpec,
  type DestinationResolver,
  type DeploymentResolver,
  type HopDestination,
  type StatusListener,
} from "./script-tenant.js";
import { LocalHub } from "./local-hub.js";
import type { BrokerSignalDeps } from "./broker-signal.js";
import type { HostView } from "./devtools/host-view.js";
import {
  coerceLegacySaveState,
  supportsExecHooks,
  supportsPpuAccess,
  supportsAudioSynth,
  supportsAudioCapture,
  supportsBattery,
  parseVgm,
  VgmSynth,
  VgmRecorder,
  measureActiveFrameSpan,
  type EmulatorCore, type NesEmulatorCore, type GbEmulatorCore,
  type GbBackgroundTilemap, type System,
  type BaseCore, type AudioSynthCapable, type ChipId, type VgmFile,
} from "@multinostalgia/core-contract";
import {
  FrameEventGate,
  PausedBackdrop,
  overscanForSystem,
  applyOverscanMask,
  type MunosCompiler,
} from "@multinostalgia/munos";
import { BusConnection, type BusSocketFactory, type BusRider } from "./bus-connection.js";

// Default/requested audio rate when no live AudioContext rate is supplied
// (headless tests). The real player threads the AudioContext's settled
// rate in via MunosScriptHostOptions.audioSampleRate; the per-frame synth
// pull is derived from that per instance (this.synthSamplesPerFrame).
const AUDIO_SAMPLE_RATE = 48000;

// Safety cap on a one-time sound render (below). SFX and jingles are short;
// this only stops a pathological VGM that never reports `finished` from
// rendering forever. 30 s at 60 fps.
const MAX_RENDER_FRAMES = 60 * 30;

// A core that can act as a standalone sound synth, built on demand to
// play a VGM (docs/sound.md). One synth chip ↔ one such core.
type SynthCore = BaseCore & AudioSynthCapable;

// One sounding voice: a PRE-RENDERED PCM buffer being mixed out frame by
// frame. A VGM is synthesized to PCM exactly ONCE (the first time it plays,
// then cached per sound — see pcmForFile), so the per-frame hot path is just
// buffer mixing: no synth core is stepped while a sound is audible. That is
// the whole point — the old model ran a full APU (~30k apu.cycle() calls)
// per voice per frame on the emulator's own thread, so a few concurrent
// voices stole the frame budget and dropped the game's framerate. Mixing
// cached buffers makes polyphony nearly free, so it stays unbounded.
interface PcmVoice {
  pcm: Float32Array;   // interleaved-stereo samples @ AUDIO_SAMPLE_RATE
  pos: number;         // read cursor into pcm (float index; stays stereo-aligned)
  loop: boolean;       // restart from 0 at the end instead of finishing
  finished: boolean;   // set when a one-shot reaches the end → reaped next frame
}

// Register write that silences a chip, applied when a core returns to the
// pool so a reused core carries no stale channel state into the next
// sound. NES: $4015 = 0 disables all channels. GB: $FF26 = 0 powers the
// APU off (clearing every sound register); the next VGM powers it back on.
const SILENCE_WRITE: Record<ChipId, readonly [number, number]> = {
  "nes-2a03": [0x15, 0x00],
  "gb-dmg": [0x16, 0x00],
};

// Sum `add` into `base` (interleaved stereo) WITHOUT clamping — the DC
// removal below needs the unclipped sum. `base` is mutated in place when
// it's long enough; null base adopts `add` (which is a fresh drain buffer).
function addAudio(base: Float32Array | null, add: Float32Array | null): Float32Array | null {
  if (add === null) return base;
  if (base === null) return add;
  const out = base.length >= add.length ? base : (() => {
    const b = new Float32Array(add.length); b.set(base); return b;
  })();
  for (let i = 0; i < add.length; i++) out[i] = out[i]! + add[i]!;
  return out;
}

// Remove the buffer's DC offset (mean), then soft-clamp to [-1, 1], in
// place. The APU streams sit well above zero (~[0, 0.85]); summed they'd
// peak past 1.0 and clip. Subtracting the mean recenters on zero so the
// real waveform survives, and the clamp only catches rare loud peaks. The
// audio worklet does NOT DC-block, so this is where it has to happen.
function removeDcAndLimit(buf: Float32Array): void {
  if (buf.length === 0) return;
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i]!;
  const mean = sum / buf.length;
  for (let i = 0; i < buf.length; i++) {
    let v = buf[i]! - mean;
    if (v > 1) v = 1; else if (v < -1) v = -1;
    buf[i] = v;
  }
}

// FNV-1a hash of a battery-save region, used to skip re-persisting an unchanged
// save on the periodic flush. Not cryptographic — a fast change detector over
// an 8-32 KB buffer; a length change alone changes the hash (seeded by length).
function batterySig(data: Uint8Array): string {
  let h = 0x811c9dc5 ^ data.length;
  for (let i = 0; i < data.length; i++) {
    h ^= data[i]!;
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

// Builds a tenant from the host's shared-game context, its spec, and its
// load-order index. Defaults to a plain ScriptTenant; tests inject a
// factory that builds a fake tenant.
export type TenantFactory =
  (ctx: TenantContext, spec: TenantSpec, index: number) => ScriptTenant;

export interface MunosScriptHostOptions {
  // The emulator core to run. Construct one with the desired backend
  // before passing it in; the host calls loadROM() during start().
  core: EmulatorCore;
  // Raw ROM bytes (iNES for NES, Game Boy cartridge for GB). Required.
  rom: Uint8Array;
  // The scripts to run as tenants, in load order (index 0 is the
  // "authored" tenant devtools inspects). At least one.
  tenants: TenantSpec[];
  // Fired after every completed frame with the composited framebuffer.
  // Buffer is reused — copy if you retain it.
  onFrame: (fb: Uint32Array) => void;
  // Fired once per frame with interleaved-stereo Float32 samples.
  onAudio?: (samples: Float32Array) => void;
  // Status sink shared by every tenant.
  onStatus?: StatusListener;
  // What max_clients() reports to scripts. Must match the server.
  maxClients?: number;
  // Hex SHA-1 of the loaded ROM, returned to scripts by local_rom_hash().
  romHash?: string;
  // Resolver invoked when a tenant calls spawn_local() — fetches one child
  // mod's source. See ScriptTenant.
  resolveDestination?: DestinationResolver;
  // Resolver invoked on a group hop (a tenant's hop() or a server redirect):
  // fetches the destination endpoint's FULL served array so the host can
  // reconcile the running tenant set against it. See reconcileHop.
  resolveDeployment?: DeploymentResolver;
  // Factory for constructing tenants (tests override it with a fake).
  tenantFactory?: TenantFactory;
  // Compiler backend for both the client halves (tenants) and, when this host
  // owns its LocalHub, the singleplayer server halves. Production wires the
  // Rust/WASM compiler here (main.ts loads it once at boot); omitted → the
  // in-process TypeScript reference. A shared LocalHub carries its own compiler
  // (main.ts sets it), so this only reaches an owned hub.
  compiler?: MunosCompiler;
  // Build a dedicated synth core for a sound chip (docs/sound.md §3) — a
  // core of the chip's system with emulateSound: true and renderPixels:
  // false. Supplied by the caller (it owns the core registry). When
  // absent, play_sound is a logged no-op: scripts stay harmless, just
  // silent. Audio is shared, so the synth lives on the supervisor, not a
  // tenant.
  makeSynthCore?: (chip: ChipId) => SynthCore | null;
  // The sample rate the audio sink actually runs at (the AudioContext's
  // settled rate — see AudioMixer.getSampleRate). The synth path renders
  // and length-matches at this rate so it stays 1:1 with the worklet's
  // no-resample ring. Defaults to AUDIO_SAMPLE_RATE (48 kHz) when omitted
  // (headless tests, callers with no live AudioContext).
  audioSampleRate?: number;
  // Factory for the per-URL bus sockets. Defaults to real WebSockets; tests
  // inject a fake so the shared-bus transport is exercised headlessly.
  socketFactory?: BusSocketFactory;
  // Tick rate (Hz) for the in-page singleplayer LocalServer — the relay
  // heartbeat that paces recorded/peer ghost updates to the renderer. Omitted →
  // LocalServer's 30 Hz default. Lower it (e.g. 3) to widen the gaps the ghost
  // renderer must coast + smooth across, for playtesting smoothness locally.
  localTickHz?: number;
  // The shared in-page local-server world (docs/p2p.md §split-screen). When
  // several tiles are meant to share one hosted game — couch players in one
  // world, any of whom can Share it to remote friends — main.ts hands every
  // tile the SAME LocalHub. Omitted → this supervisor privately owns a hub
  // (the singleplayer / N=1 case, identical to the old per-tile server).
  localHub?: LocalHub;
  // Durable battery-save (SRAM) persistence for this game, host-managed and
  // keyed by the caller (ROM + script bundle — see docs/battery-save-persistence.md).
  // The host restores the save after loadROM (before the first frame), flushes
  // it on a dirty-checked cadence + at teardown, and lets a mod suppress the
  // write-back via the battery_persist builtin. Omitted → no persistence (the
  // headless/test path); a non-battery cartridge is skipped regardless.
  batterySave?: BatterySaveHooks;
}

// The host-side port for persisting a cartridge's battery save. The
// implementation (main.ts) owns the key (ROM hash + script bundle) and the
// durable store; the host owns the lifecycle (when to load, when to flush) and
// the mod-facing on/off toggle. See docs/battery-save-persistence.md.
export interface BatterySaveHooks {
  // Load the persisted save image for the given bundle, or null when none is
  // stored. `bundleId` identifies the running script set (the host derives it
  // from the live tenants) so each bundle keeps its own save; the implementation
  // folds it into the storage key. Called on start and on every bundle change.
  load(bundleId: string): Promise<Uint8Array | null>;
  // Persist the current save image under `bundleId`. Fire-and-forget — the host
  // calls it on a throttled dirty-checked cadence, on a bundle change, and at
  // teardown.
  save(bundleId: string, data: Uint8Array): void;
}

export class MunosScriptHost implements HostView {
  private readonly core: EmulatorCore;
  private readonly rom: Uint8Array;
  private readonly screenW: number;
  private readonly screenH: number;
  private readonly overscan: number;
  // Composite framebuffer — game pixels plus every tenant's overlay draws.
  // Reused each frame; passed to userOnFrame in place.
  private readonly framebuffer: Uint32Array;
  private readonly pausedBackdrop = new PausedBackdrop();
  private readonly frameGate = new FrameEventGate();
  private readonly userOnFrame: (fb: Uint32Array) => void;
  private readonly userOnAudio: ((samples: Float32Array) => void) | null;
  private readonly onStatus: StatusListener;
  private readonly maxClients: number;
  private readonly romHash: string;
  private readonly resolveDestination: DestinationResolver | null;
  private readonly resolveDeployment: DeploymentResolver | null;
  // Monotonic hop generation: a newer group hop supersedes an in-flight one,
  // so a slow array fetch can't reconcile against a stale destination.
  private reconcileGen = 0;
  // The URL where the CURRENT game (script set) was established — the stable
  // deployment root a fresh player would enter, e.g. `wss://server/super_mario_bros`.
  // Seeded from the initial load and updated by reconcileHop ONLY when the script
  // SET changes (a lobby→game hop), NOT on a same-set rehome (a game→shard hop —
  // the primary redirecting a player to a per-level worker keeps the members
  // byte-identical). So it names the game's front door, never a leaf shard: a
  // shared-server invite advertises this so a friend enters the same game and is
  // routed fresh by the server (docs/p2p.md §"Server players invite too").
  private currentGameUrl: string | null = null;
  private readonly tenantFactory: TenantFactory;
  private readonly compiler: MunosCompiler | undefined;
  // The one context every tenant shares — reused for runtime spawns.
  private readonly tenantCtx: TenantContext;
  // One shared socket per server URL (docs/multi-script-client.md §"the
  // connection is a shared bus"). Tenants join/leave as the forest changes;
  // a bus is dropped once its last rider leaves.
  private readonly buses = new Map<string, BusConnection>();
  private readonly socketFactory: BusSocketFactory | undefined;
  private readonly localTickHz: number | undefined;
  // The in-page local-server world (server halves + P2P router + hosting,
  // docs/p2p.md / multi-script-client.md §"The lobby's role"). Shared across
  // tiles when main.ts injects one hub (couch players in one game); privately
  // owned otherwise (the singleplayer / N=1 case). `ownsHub` gates teardown: an
  // owned hub is reset when this supervisor's local play ends, whereas a shared
  // one is torn down by its owner (main.ts, at a fresh-game boundary), never here.
  private readonly localHub: LocalHub;
  private readonly ownsHub: boolean;

  // Live tenants in load/spawn order — composite + input fan-out iterate
  // this (later entries paint on top).
  protected readonly tenants: ScriptTenant[] = [];
  // Forest topology. parent === null = a top-level tenant (a child of the
  // implicit immortal orchestrator — the array front door); a spawned
  // tenant points at its spawner. Handles are the script-facing despawn
  // tokens.
  private readonly parentOf = new Map<ScriptTenant, ScriptTenant | null>();
  private readonly handleOf = new Map<ScriptTenant, number>();
  private readonly byHandle = new Map<number, ScriptTenant>();
  // Spawns whose source fetch is still in flight; despawning before the
  // load completes cancels them. Keyed by the handle returned synchronously.
  private readonly pending = new Map<number, { parent: ScriptTenant | null; canceled: boolean }>();
  private nextHandle = 1;

  private rafHandle: number | null = null;
  private stopped = false;

  // ----- battery save (docs/battery-save-persistence.md) -----
  // The durable-save port (null when persistence isn't wired — headless/tests).
  private readonly batterySave: BatterySaveHooks | null;
  // Host auto-write-back toggle, a shared-game latch driven by the Munos
  // battery_persist builtin. Default on; a cloud-save mod turns it off to take
  // ownership of the save (last-writer-wins across tenants — see the builtin).
  private batteryPersistEnabled = true;
  // FNV-1a of the last-persisted save region, so a periodic flush that finds no
  // change writes nothing. "" until the first restore/flush establishes it.
  private lastBatterySig = "";
  // The bundle whose save currently occupies the cartridge's battery RAM (the
  // key flushes go to). Re-derived on every restore; a mismatch with
  // currentBundleId() means a hop/spawn swapped the game → re-key.
  private batteryBundleId = "";
  // Guards tickBattery so an async re-key can't overlap itself.
  private batteryBusy = false;
  // The cartridge's pristine power-on save RAM (captured at loadROM), restored
  // when switching to a bundle that has no stored save so it reads as blank.
  private blankBattery: Uint8Array | null = null;
  // Frames since the last battery tick; the dirty check + bundle-change check
  // run every BATTERY_FLUSH_FRAMES so the getBattery() copy isn't taken every frame.
  private batteryFlushCounter = 0;
  private static readonly BATTERY_FLUSH_FRAMES = 30; // ~0.5s at 60fps

  // ROM pause has two independent sources: tenant pause_rom requests
  // (refcounted — any tenant holding a request keeps the game frozen) and
  // a devtools pause. The game runs only when both are clear.
  private pauseRefcount = 0;
  private pausedByDevtools = false;

  // ----- sound (docs/sound.md) — polyphonic shared output -----
  // Each sounding voice mixes a pre-rendered PCM buffer; there is no shared
  // channel pool to exhaust, so play_sound is never downprioritized
  // (docs/sound.md §"Voices & polyphony"). One-shot voices are reaped at the
  // end of their buffer.
  private readonly activeVoices: PcmVoice[] = [];
  // Rendered-PCM cache, keyed by the parsed VGM. A sound (sound_from_vgm
  // returns a stable VgmFile) is synthesized to samples ONCE on first play
  // and replayed from the buffer forever after — so repeated/overlapping
  // shots cost nothing but a buffer mix.
  private readonly pcmCache = new Map<VgmFile, Float32Array>();
  // Per-chip pool of idle synth cores, reused across the (now one-time)
  // renders so a first burst of distinct SFX doesn't churn whole-console
  // allocations. Grows on demand (no cap); drains back here after each render.
  private readonly idleCores = new Map<ChipId, SynthCore[]>();
  // The devtools "Play" voice, tracked so its Stop/▶ toggle targets that
  // one specifically (script-fired voices are anonymous in activeVoices).
  private devtoolsVoice: PcmVoice | null = null;
  private readonly makeSynthCore: ((chip: ChipId) => SynthCore | null) | null;
  // The audio sink's actual sample rate and the per-frame synth pull
  // derived from it (see MunosScriptHostOptions.audioSampleRate).
  private readonly audioSampleRate: number;
  private readonly synthSamplesPerFrame: number;
  private warnedNoSynthFactory = false;
  // Devtools sound capture: a recorder tapping the shared GAME core's APU
  // writes. Distinct from playback (which drives a separate synth core).
  private soundRecorder: VgmRecorder | null = null;
  private soundCapturing = false;

  constructor(opts: MunosScriptHostOptions) {
    this.core = opts.core;
    this.rom = opts.rom;
    this.userOnFrame = opts.onFrame;
    this.userOnAudio = opts.onAudio ?? null;
    this.onStatus = opts.onStatus ?? (() => {});
    this.maxClients = opts.maxClients ?? 64;
    this.romHash = opts.romHash ?? "";
    this.resolveDestination = opts.resolveDestination ?? null;
    this.resolveDeployment = opts.resolveDeployment ?? null;
    this.makeSynthCore = opts.makeSynthCore ?? null;
    this.batterySave = opts.batterySave ?? null;
    this.socketFactory = opts.socketFactory;
    this.localTickHz = opts.localTickHz;
    // Share the injected hub (couch split-screen) or privately own one (N=1).
    this.compiler = opts.compiler;
    this.ownsHub = opts.localHub === undefined;
    this.localHub = opts.localHub ?? new LocalHub({
      maxClients: this.maxClients,
      ...(this.localTickHz !== undefined ? { tickHz: this.localTickHz } : {}),
      ...(this.compiler !== undefined ? { compiler: this.compiler } : {}),
    });
    // Synthesize at the sink's real rate so audio stays 1:1 with the
    // no-resample worklet ring; fall back to the 48 kHz default headless.
    this.audioSampleRate = opts.audioSampleRate ?? AUDIO_SAMPLE_RATE;
    this.synthSamplesPerFrame = Math.round(this.audioSampleRate / 60);
    this.screenW = this.core.screenWidth;
    this.screenH = this.core.screenHeight;
    this.overscan = overscanForSystem(this.core.system);
    this.framebuffer = new Uint32Array(this.screenW * this.screenH);

    if (opts.tenants.length === 0) {
      throw new Error("MunosScriptHost requires at least one tenant");
    }
    // The default factory injects the host's compiler into every tenant spec
    // that doesn't already name one, so the client halves compile through the
    // same backend as the server halves. A test-supplied factory is used as-is.
    this.tenantFactory = opts.tenantFactory ??
      ((c, s) => new ScriptTenant(
        c,
        this.compiler !== undefined && s.compiler === undefined
          ? { ...s, compiler: this.compiler }
          : s,
      ));
    this.tenantCtx = this.makeTenantContext();
    // Register the initial set as top-level tenants (children of the
    // implicit orchestrator); the first donates its identity per
    // docs/multi-script-client.md. They're started in start().
    for (const spec of opts.tenants) this.registerTenant(spec, null);
    // Seed the current-game URL from the initial load (the entry the player
    // starts at, e.g. the server's lobby); hops refine it as games are entered.
    this.currentGameUrl = opts.tenants[0]?.url ?? null;
  }

  static getAudioSampleRate(): number { return AUDIO_SAMPLE_RATE; }

  // The one TenantContext every tenant shares — the shared-game surface is
  // identical for all of them (one core, one pause state).
  private makeTenantContext(): TenantContext {
    return {
      readCpuByte: (a) => this.core.readCpuByte(a),
      writeCpuByte: (a, v) => this.core.writeCpuByte(a, v),
      acquirePause: () => this.acquirePause(),
      releasePause: () => this.releasePause(),
      screenW: this.screenW,
      screenH: this.screenH,
      onStatus: this.onStatus,
      resolveDestination: this.resolveDestination,
      maxClients: this.maxClients,
      romHash: this.romHash,
      // Execution hooks drive the shared core's ExecHookCapable surface
      // (no-ops when the core lacks support). The `fire` closure a tenant
      // passes re-enters its OWN wasm — see ScriptTenant.invokeExecCallback.
      onExecHook: (target, fire, priority) => {
        if (!supportsExecHooks(this.core)) {
          console.error(`[munos] on_exec: core '${this.core.system}' has no execution-hook support`);
          return 0;
        }
        return this.core.onExecHook(target, fire, priority);
      },
      clearExecHook: (handle) => {
        if (supportsExecHooks(this.core)) this.core.clearExecHook(handle);
      },
      readReg: (reg) => supportsExecHooks(this.core) ? this.core.readCpuReg(reg) : 0,
      writeReg: (reg, val) => {
        if (supportsExecHooks(this.core)) { this.core.writeCpuReg(reg, val); return; }
        console.error(`[munos] write_reg: core '${this.core.system}' has no execution-hook support — write dropped (stale build?)`);
      },
      writeRom: (coord, byte) => {
        if (supportsExecHooks(this.core)) { this.core.writeRomByte(coord, byte); return; }
        console.error(`[munos] write_rom: core '${this.core.system}' has no execution-hook support — patch dropped (stale build?)`);
      },
      readPpu: (addr) => supportsPpuAccess(this.core) ? this.core.readPpuByte(addr) : 0,
      writePpu: (addr, value) => {
        if (supportsPpuAccess(this.core)) { this.core.writePpuByte(addr, value); return; }
        console.error(`[munos] write_ppu: core '${this.core.system}' has no PPU-access support — write dropped (stale build?)`);
      },
      spawnTenant: (url, handoff, parent, local, initData) => this.spawnChild(url, handoff, parent, local ?? false, initData ?? new Uint8Array(0)),
      despawnTenant: (handle, requester) => this.despawnChild(handle, requester),
      hopGroup: (url, handoff) => { void this.reconcileHop(url, handoff); },
      attachToBus: (rider, url) => this.attachToBus(rider, url),
      detachFromBus: (rider, bus) => this.detachFromBus(rider, bus),
      // Sound is shared (one audio output): a tenant hands its parsed VGM
      // here to voice it. Single shared voice — last play wins.
      playSound: (file, loop) => this.playSound(file, loop),
      // Battery-save write-back toggle (battery_persist). Shared-game latch:
      // any tenant can turn the host's auto-save off (a cloud-save mod owning
      // the file) or back on; last writer wins.
      setBatteryPersist: (enabled) => { this.batteryPersistEnabled = enabled; },
    };
  }

  // ----- forest: register / spawn / despawn -----

  // Build a tenant from a spec and record it in the forest. `parent` null =
  // a top-level tenant. `handle` reuses a pre-allocated token (a pending
  // spawn); otherwise a fresh one is minted. Does NOT start it — the caller
  // does (start() for the initial set; spawnChild for runtime spawns).
  private registerTenant(spec: TenantSpec, parent: ScriptTenant | null, handle?: number): ScriptTenant {
    const tenant = this.tenantFactory(this.tenantCtx, spec, this.tenants.length);
    const h = handle ?? this.nextHandle++;
    this.tenants.push(tenant);
    this.parentOf.set(tenant, parent);
    this.handleOf.set(tenant, h);
    this.byHandle.set(h, tenant);
    return tenant;
  }

  // Spawn a child mod (or a whole deployment) of `parent`: fetch the member
  // set, host each server half locally, register + start each client half.
  // Returns a handle synchronously; the load runs async, and despawning the
  // handle mid-load cancels it.
  //
  // A LOCAL spawn mounts the endpoint's FULL served array (a deployment → its
  // members; a bare mod → one), so a gameplay bundle stands up all its halves
  // in-page over the shared local bus — the Fork A private-launch path
  // (docs/level-player-sessions.md §6). `initData` seeds every member's
  // `event init` (the per-instance blob → the store adopts the level; §4), the
  // local-mount mirror of the real server's spawn(..., data, ...). A networked
  // spawn stays single-mod (there is no local server to host a member array
  // onto, and a remote instance can't be seeded from here).
  private spawnChild(
    url: string, handoff: Uint8Array, parent: ScriptTenant,
    local: boolean, initData: Uint8Array,
  ): number {
    if (!this.resolveDestination && !this.resolveDeployment) {
      console.error("[munos] spawn_local: no resolver wired — cannot fetch a mod to spawn");
      return 0;
    }
    const handle = this.nextHandle++;
    this.pending.set(handle, { parent, canceled: false });
    void (async () => {
      // Resolve the member set. A local mount uses the DEPLOYMENT resolver (the
      // full served array — a deployment's members, or a one-element array for a
      // bare mod), so a gameplay bundle mounts whole. Fall back to the single-mod
      // resolver when the deployment resolver isn't wired (e.g. a test host) or
      // for a networked spawn.
      let members: HopDestination[] | null = null;
      try {
        if (local && this.resolveDeployment) {
          const served = await this.resolveDeployment(url);
          members = served ? [...served] : null;
        } else if (this.resolveDestination) {
          const one = await this.resolveDestination(url);
          members = one ? [one] : null;
        } else {
          console.error("[munos] spawn_local: only a deployment resolver is wired, but this is a networked spawn");
        }
      } catch (e) {
        console.error(`[munos] spawn_local: resolving ${url} threw:`, e);
      }
      const entry = this.pending.get(handle);
      this.pending.delete(handle);
      if (!entry || entry.canceled) return; // despawned before the load finished
      // The supervisor may have been torn down while the source was fetching.
      // Bail before touching the hub / registering a tenant — otherwise a spawn
      // that resolves post-stop() opens a bus nothing will close and (for a
      // local: spawn) re-stands-up the LocalServer the reset just tore down.
      if (this.stopped) return;
      if (!members || members.length === 0) {
        console.error(`[munos] spawn_local: could not load ${url}`);
        return;
      }
      // Singleplayer (`local:`): stand up the in-browser server (once) and host
      // EVERY member's SERVER half on it — seeded with initData (→ event init) —
      // before any client half connects, so a co-launched listener (e.g. the
      // recorder) and a pre-seeded store are already live when the first frames
      // flow. Server-side scope is the bare label, matching the real server
      // (docs/persistence.md). A member with an empty server block becomes an
      // inert member — harmless.
      if (local) {
        try {
          await this.localHub.ensureServer();
          // stop() may have landed during ensureServer(), which re-memoizes the
          // server that a teardown reset just cleared. Undo that resurrection
          // (only if we own the hub) and bail rather than adding a member to it.
          if (this.stopped) {
            if (this.ownsHub) this.localHub.reset();
            return;
          }
          for (const m of members) this.localHub.addMember(m.source, m.label, m.label, initData);
        } catch (e) {
          console.error(`[munos] spawn_local: local server bring-up for ${url} failed:`, e);
          return;
        }
      }
      // Register + start each member's CLIENT half. The first member owns the
      // returned `handle` (a child of `parent`); the rest are children of the
      // first, so one `despawn(handle)` tears the whole mounted subtree down.
      // All ride the local bus (for a local mount) and carry the same handoff.
      let first: ScriptTenant | null = null;
      for (const m of members) {
        if (this.stopped) return;
        const spec: TenantSpec = {
          source: m.source, url, scriptLabel: m.label, handoff, localBus: local,
        };
        const tenant: ScriptTenant = first === null
          ? this.registerTenant(spec, parent, handle)
          : this.registerTenant(spec, first);
        if (first === null) first = tenant;
        try {
          await tenant.start();
        } catch (e) {
          console.error(`[munos] spawn_local: ${m.label} (${url}) start failed:`, e);
          this.removeTenant(tenant);
        }
      }
    })();
    return handle;
  }

  // Despawn a tenant the `requester` owns (a descendant in its subtree),
  // and that tenant's own subtree. A handle outside the requester's subtree
  // is refused — the isolation guarantee (mods can't eject siblings).
  private despawnChild(handle: number, requester: ScriptTenant): void {
    const pend = this.pending.get(handle);
    if (pend) {
      const owned = pend.parent === requester ||
        (pend.parent !== null && this.isDescendantOf(pend.parent, requester));
      if (owned) { pend.canceled = true; this.pending.delete(handle); }
      else console.error("[munos] despawn: handle not owned by caller");
      return;
    }
    const target = this.byHandle.get(handle);
    if (!target) return; // already gone / unknown handle
    if (!this.isDescendantOf(target, requester)) {
      console.error("[munos] despawn: handle not in caller's subtree");
      return;
    }
    this.removeTenant(target);
  }

  // Tear down a tenant and its entire subtree (descendants die with their
  // parent), cancelling any in-flight spawns rooted in the removed set.
  private removeTenant(root: ScriptTenant): void {
    const doomed = this.subtreeOf(root); // includes root
    const doomedSet = new Set(doomed);
    for (const [h, p] of this.pending) {
      if (p.parent && doomedSet.has(p.parent)) { p.canceled = true; this.pending.delete(h); }
    }
    for (const t of doomed) {
      t.stop();
      const h = this.handleOf.get(t);
      if (h !== undefined) this.byHandle.delete(h);
      this.handleOf.delete(t);
      this.parentOf.delete(t);
      const idx = this.tenants.indexOf(t);
      if (idx >= 0) this.tenants.splice(idx, 1);
    }
  }

  // root + all descendants (breadth-first over the parent map).
  private subtreeOf(root: ScriptTenant): ScriptTenant[] {
    const out: ScriptTenant[] = [root];
    for (let i = 0; i < out.length; i++) {
      const node = out[i]!;
      for (const t of this.tenants) {
        if (this.parentOf.get(t) === node && !out.includes(t)) out.push(t);
      }
    }
    return out;
  }

  private isDescendantOf(node: ScriptTenant, ancestor: ScriptTenant): boolean {
    let p = this.parentOf.get(node) ?? null;
    while (p !== null) {
      if (p === ancestor) return true;
      p = this.parentOf.get(p) ?? null;
    }
    return false;
  }

  // ----- group hop: reconcile the running forest against a new endpoint -----
  //
  // A hop (a tenant's hop() or a server-pushed redirect) re-points the ONE
  // shared connection, so it's a whole-group operation (docs/multi-script-
  // client.md §"Hopping"). Fetch the destination endpoint's served array, then
  // diff it against the currently-running TOP-LEVEL tenants by identity:
  //   - label absent from the new array, or its source changed → evict that
  //     tenant (and its spawned subtree);
  //   - label present AND byte-identical → keep it running, just re-point it
  //     onto the destination's shared bus (every global / image preserved);
  //   - served label with no running match → start it fresh on the new bus.
  // Spawned children of a surviving tenant ride along (they're its subtree);
  // children of an evicted tenant die with it. A newer hop supersedes a slow
  // one via reconcileGen.
  private async reconcileHop(url: string, handoff: Uint8Array): Promise<void> {
    if (!this.resolveDeployment) {
      console.error("[munos] hop: no deployment resolver wired — cannot reconcile");
      return;
    }
    // Claim the latest generation up front so a concurrent EARLIER hop aborts
    // before it does work. But remember the prior gen: a hop that fails to
    // resolve a destination must NOT keep that claim, or it silently supersedes
    // a still-in-flight valid hop (issue A, then a broken B — B bumps the gen,
    // bails, and A's own gen-check then drops it). On failure we roll the claim
    // back, which is safe precisely because the guard below proves we're still
    // the latest generation (a newer hop would have made us abort instead).
    const prevGen = this.reconcileGen;
    const gen = ++this.reconcileGen;
    let served: readonly HopDestination[] | null = null;
    try {
      served = await this.resolveDeployment(url);
    } catch (e) {
      console.error(`[munos] hop: resolving ${url} threw:`, e);
    }
    if (gen !== this.reconcileGen || this.stopped) return; // superseded / torn down
    if (!served || served.length === 0) {
      this.reconcileGen = prevGen; // release the claim so a concurrent valid hop survives
      this.onStatus("error", `hop to ${url} failed (no scripts served)`);
      return;
    }
    this.onStatus("info", `hopping to ${url}…`);

    // Diff by (label, source). A running top-level tenant survives only if the
    // served set still carries a byte-identical script under its label.
    const servedByLabel = new Map(served.map(s => [s.label, s]));
    const topLevel = (): ScriptTenant[] =>
      this.tenants.filter(t => this.parentOf.get(t) === null);
    const topLevelBefore = topLevel().length;
    const survivors = new Set<string>();
    for (const t of topLevel()) {
      const match = servedByLabel.get(t.label);
      if (match && match.source === t.sourceText) survivors.add(t.label);
      else this.removeTenant(t); // evict it + its spawned subtree (leaves its bus)
    }

    // Did the script SET change (a lobby→game hop), or is this a same-set rehome
    // (a game→shard hop)? An eviction or a genuinely-new script means the set
    // changed; a pure rehome keeps every member. Only a set change advances the
    // current-game URL, so it names the game's stable front door, not a leaf shard.
    if (survivors.size !== topLevelBefore || survivors.size !== served.length) {
      this.currentGameUrl = url;
    }

    // Re-point survivors onto the destination's shared bus (state preserved).
    // After the eviction pass, every remaining top-level tenant is a survivor.
    for (const t of topLevel()) t.rehome(url, handoff);

    // Start the genuinely-new scripts as fresh top-level tenants on that bus.
    for (const s of served) {
      if (survivors.has(s.label)) continue;
      if (gen !== this.reconcileGen || this.stopped) return; // a newer hop won
      const spec: TenantSpec = { source: s.source, url, scriptLabel: s.label, handoff };
      const tenant = this.registerTenant(spec, null);
      try {
        await tenant.start();
      } catch (e) {
        console.error(`[munos] hop: starting '${s.label}' failed:`, e);
        this.removeTenant(tenant);
      }
    }
  }

  // host_locally (docs/node-federation.md, Stage C): the remote instance told
  // us to host the current deployment on THIS machine and keep it as our uplink
  // parent. Same reconcile shape as a hop, but the destination is our own
  // machine: bring up the local server, spin up each running script's SERVER
  // half locally (its source is already in hand — it's what we run as a
  // client), wire the local server's uplink to the parent, then rehome our
  // client tenants onto the local loopback. From here editing is local and only
  // an upload bubbles up the uplink to the parent.
  //
  // NOTE: the end-to-end browser flow (this + a real central server) is
  // validated by the manual two-window test; the unit layer covers the
  // observable orchestration (members hosted, parent adopted, tenants rehomed).
  private async reconcileHostLocally(parentUrl: string, handoff: Uint8Array): Promise<void> {
    if (this.stopped) return;
    // Same gen discipline as reconcileHop: claim the latest generation, but keep
    // the prior one so a FAILED host-locally doesn't keep the claim and silently
    // supersede a still-in-flight valid hop (whose own gen-check would then drop
    // it, stranding the game in neither state). Roll back only if we're still the
    // latest — a newer op would have bumped past us and must not be clobbered.
    const prevGen = this.reconcileGen;
    const gen = ++this.reconcileGen;
    try {
      await this.localHub.ensureServer();
    } catch (e) {
      console.error("[munos] host_locally: bringing up the local server threw:", e);
      if (gen === this.reconcileGen) this.reconcileGen = prevGen;
      if (!this.stopped) this.onStatus("error", "hosting locally failed (local server did not start)");
      return;
    }
    if (gen !== this.reconcileGen || this.stopped) return; // superseded / torn down
    this.onStatus("info", "hosting locally…");
    // Wire the uplink first, so an upload the instant editing starts has a parent.
    this.localHub.adoptParent(parentUrl, handoff);
    const topLevel = this.tenants.filter(t => this.parentOf.get(t) === null);
    // Host each running script's SERVER half locally (idempotent per label).
    for (const t of topLevel) this.localHub.addMember(t.sourceText, t.label, t.label);
    // Move our client halves off the remote bus onto the local loopback. Use
    // rehomeLocal (not rehome(LOCAL_BUS_KEY)) so each tenant KEEPS its real
    // origin url — hostedModUrls() then still advertises this editor to a P2P
    // joiner / couch [+] tile, and only the bus goes local.
    for (const t of topLevel) t.rehomeLocal(handoff);
  }

  // ----- shared-bus transport -----

  // Join `rider` to the bus for `url`, creating it on the first rider. The
  // tenant holds the returned bus to send through it. One socket per URL: a
  // second tenant connecting to the same URL shares the first's socket.
  private attachToBus(rider: BusRider, url: string): BusConnection {
    let bus = this.buses.get(url);
    if (!bus) {
      // The local bus is backed by the in-page LocalServer via a managed
      // loopback (the hub allocates this tile's slot on the shared server); every
      // other URL opens a real WebSocket through the configured factory.
      // ensureServer() ran before this rider's start() (spawnChild awaits it), so
      // the hub is ready when the key is LOCAL_BUS_KEY.
      const factory: BusSocketFactory | undefined =
        url === LOCAL_BUS_KEY && this.localHub.ready
          ? () => this.localHub.createLocalSocket()
          : this.socketFactory;
      // A redirect on any bus is a GROUP hop — reconcile the whole forest
      // against the destination's served array (the bus passes a URL already
      // resolved against its own). All buses share this one handler.
      bus = new BusConnection(
        url, factory, this.onStatus,
        (to, handoff) => { void this.reconcileHop(to, handoff); },
        (parentUrl, handoff) => { void this.reconcileHostLocally(parentUrl, handoff); },
      );
      this.buses.set(url, bus);
    }
    bus.join(rider);
    return bus;
  }

  // Remove `rider` from its bus; drop the bus once empty (its socket closes
  // with the last rider, so a mod leaving never strands a co-rider).
  private detachFromBus(rider: BusRider, bus: BusConnection): void {
    bus.leave(rider);
    if (bus.riderCount === 0) {
      this.buses.delete(bus.url);
      // This tile's local play ended (its loopback closed with the bus, freeing
      // its slot on the shared server). If this supervisor OWNS its hub (N=1),
      // tear the world down so a later launch starts clean; a SHARED hub is left
      // up — other tiles are still in the game — for main.ts to reset.
      if (bus.url === LOCAL_BUS_KEY && this.ownsHub) {
        this.localHub.reset();
      }
    }
  }

  get system(): System { return this.core.system; }

  // ----- P2P hosting (docs/p2p.md) — delegated to the (shared) LocalHub -----
  //
  // The hostable server, its P2P router, and the broker session all live on the
  // hub, so hosting is couch-wide: any tile sharing the shared hub shares the
  // ONE in-page game (its couch players + the remote joiners are all clients of
  // one server). These delegate so the Share UI can keep calling through any
  // tile's host.

  // Whether the shared game can be shared right now (a server half is live
  // in-page). In normal remote play the local server is empty, so it's off.
  canHost(): boolean {
    return this.localHub.canHost();
  }

  isHosting(): boolean {
    return this.localHub.isHosting();
  }

  // Live remote-peer count while hosting (0 otherwise) — for the share UI.
  peerCount(): number {
    return this.localHub.peerCount();
  }

  // The real origin URLs of the mods this tile runs on the local bus — the
  // client halves a joiner must fetch and run against the host. Distinct, in
  // load order. Read LIVE from the running tenants (not a load-time snapshot) so
  // it reflects a lobby→game group hop (reconcileHop): a joiner's session-info
  // request (session-info.ts) then learns the CURRENT set, not the entry lobby.
  // Per-tile (each tile runs the same launched set, so any tile answers).
  hostedModUrls(): string[] {
    const seen = new Set<string>();
    const urls: string[] = [];
    for (const t of this.tenants) {
      if (!t.onLocalBus) continue;
      const u = t.modUrl;
      if (!seen.has(u)) { seen.add(u); urls.push(u); }
    }
    return urls;
  }

  // The stable deployment-root URL of the game currently running (see the field
  // note): where a fresh player enters this game and is routed by the server.
  // Used to advertise a shared-server invite (mode "server"). null before any
  // load. For an in-page P2P host this is the local bus key and unused — that
  // path advertises hostedModUrls() instead.
  gameUrl(): string | null {
    return this.currentGameUrl;
  }

  // Start sharing the in-page game to remote peers over WebRTC via the broker.
  // Resolves with the broker session id (for the share link). Delegated to the
  // hub — the router already serves the couch tiles; this opens the broker door
  // so remote joiners take slots alongside them.
  startHosting(
    brokerUrl: string,
    deps: { brokerDeps?: BrokerSignalDeps } = {},
  ): Promise<string> {
    return this.localHub.startHosting(brokerUrl, deps);
  }

  // Open an info-ONLY broker session: answer joiners' session-info bootstrap
  // (which game + ROM) without hosting any game. For a player on a shared server
  // who invites friends to play alongside them ON THE SERVER — nothing is hosted
  // in-page, the broker session exists only to hand out the game identity so the
  // one ?join= link format serves both P2P and server invites. Resolves with the
  // broker session id for the link.
  startInfoHosting(
    brokerUrl: string,
    deps: { brokerDeps?: BrokerSignalDeps } = {},
  ): Promise<string> {
    return this.localHub.startInfoHosting(brokerUrl, deps);
  }

  // Stop sharing: close the broker session and every remote peer. The couch
  // tiles' loopbacks stay connected, so local play continues uninterrupted.
  stopHosting(): void {
    this.localHub.stopHosting();
  }

  // ----- lifecycle -----

  async start(): Promise<void> {
    this.core.loadROM(this.rom);
    // Capture the cartridge's pristine power-on save RAM as the "blank" baseline
    // (NES zero-fills PRG-RAM; GB fills cart RAM with 0xFF — core-specific), so
    // switching to a bundle that has no stored save can present a fresh cartridge
    // instead of leaking the previous bundle's save. Taken right after loadROM,
    // before any restore mutates it.
    if (supportsBattery(this.core)) {
      const b = this.core.getBattery();
      this.blankBattery = b ? new Uint8Array(b) : null;
    }
    // Restore the battery save into the freshly-loaded cartridge BEFORE any
    // tenant frame runs, so the game (and any cloud-save mod that overwrites it
    // in frame 0) sees the local file as the starting point. Non-battery carts
    // and the no-persistence path are no-ops.
    await this.restoreBattery();
    this.core.onOamDmaWrite(() => this.handleOamDma());
    // Sequential so each tenant's wasm + socket settle before the next
    // comes up (matches the old single-script start ordering).
    for (const t of this.tenants) {
      await t.start();
    }
    this.startFrameLoop();
  }

  // ----- battery save (docs/battery-save-persistence.md) -----
  //
  // The save file is keyed by which script bundle is running (so vanilla and a
  // modded run of the same cartridge keep separate saves). The bundle is the
  // set of running tenant labels — and it CHANGES at runtime: a lobby hops or
  // spawns into a game, swapping the tenant set. So the key can't be frozen at
  // construction; the host recomputes it from the live tenants and re-keys
  // (flush the outgoing save, restore the incoming one) whenever it changes.

  // Identity of the currently-running bundle: an order-independent hash of every
  // live tenant's label. Two different games/mod-sets over one cartridge hash
  // differently; the same set always hashes the same regardless of load order.
  private currentBundleId(): string {
    const labels = this.tenants.map(t => t.label).sort();
    let h = 0x811c9dc5 ^ labels.length;
    for (const label of labels) {
      for (let i = 0; i < label.length; i++) {
        h ^= label.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
      h ^= 0x2f; // '/' separator so ["ab","c"] != ["a","bc"]
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16);
  }

  // Load the persisted save for the CURRENT bundle and inject it into the
  // cartridge's battery RAM. Runs once in start() (after loadROM) and again on
  // every bundle change (see tickBattery). Skips silently when persistence isn't
  // wired, the core has no battery capability, or the cartridge has no battery
  // (getBattery() === null). Records batteryBundleId so subsequent flushes go to
  // the same key, and seeds lastBatterySig so the first flush doesn't re-write
  // an unchanged save.
  private async restoreBattery(): Promise<void> {
    if (!this.batterySave || !supportsBattery(this.core)) return;
    if (this.core.getBattery() === null) return; // non-battery cartridge
    const bundleId = this.currentBundleId();
    let blob: Uint8Array | null = null;
    try {
      blob = await this.batterySave.load(bundleId);
    } catch (e) {
      console.error("[munos] battery restore failed:", e);
    }
    // A teardown may have landed while the load was in flight.
    if (this.stopped || !supportsBattery(this.core)) return;
    if (blob) {
      this.core.setBattery(blob);
    } else if (this.blankBattery) {
      // No stored save for this bundle: present the pristine cartridge, not the
      // outgoing bundle's SRAM (which would leak one game's save into another).
      this.core.setBattery(this.blankBattery);
    }
    this.batteryBundleId = bundleId;
    const now = this.core.getBattery();
    this.lastBatterySig = now ? batterySig(now) : "";
  }

  // Persist the current save (under the bundle whose save is loaded) if it
  // changed since the last flush. `force` skips the dirty check (teardown). A
  // no-op when persistence is off — not wired, disabled by a mod
  // (battery_persist), or a non-battery cartridge.
  private maybeFlushBattery(force: boolean): void {
    if (!this.batterySave || !this.batteryPersistEnabled || !supportsBattery(this.core)) return;
    const data = this.core.getBattery();
    if (data === null) return;
    const sig = batterySig(data);
    if (!force && sig === this.lastBatterySig) return;
    this.lastBatterySig = sig;
    this.batterySave.save(this.batteryBundleId, data);
  }

  // Per-cadence battery work (called from the frame loop). If the running bundle
  // changed since the loaded save — a hop/spawn swapped the tenant set — flush
  // the outgoing bundle's save and restore the incoming one, so switching games
  // switches save files. Otherwise it's an ordinary dirty-checked flush. Guarded
  // against overlapping runs because restore is async.
  private async tickBattery(): Promise<void> {
    if (this.batteryBusy || !this.batterySave || !supportsBattery(this.core)) return;
    if (this.core.getBattery() === null) return; // non-battery cartridge
    this.batteryBusy = true;
    try {
      if (this.currentBundleId() !== this.batteryBundleId) {
        this.maybeFlushBattery(false); // save the outgoing bundle under its key
        await this.restoreBattery();   // load the incoming bundle's save
      } else {
        this.maybeFlushBattery(false);
      }
    } finally {
      this.batteryBusy = false;
    }
  }

  stop(): void {
    this.stopped = true;
    // Final battery flush before teardown, so a save made in the last second
    // (past the periodic cadence) still lands. Runs before stopped short-circuits
    // anything; a disabled toggle / non-battery cart still makes it a no-op.
    this.maybeFlushBattery(true);
    // Cancel any in-flight spawn_local loads: their async bodies check this on
    // resume so they can't register a tenant (or resurrect the local server)
    // after teardown. The stopped-recheck in spawnChild is the backstop for the
    // window between this cancel and the body's next await point.
    for (const p of this.pending.values()) p.canceled = true;
    this.pending.clear();
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    // Stopping the tenants closes their bus sockets — including this tile's
    // local loopback, which frees its slot on the (possibly shared) server.
    for (const t of this.tenants) t.stop();
    // Tear the in-page world down only if we OWN it (N=1). A SHARED hub belongs
    // to main.ts, which resets it at a fresh-game boundary — other tiles may
    // still be mid-game (and mid-share) on it, so this tile must not stop it.
    if (this.ownsHub) this.localHub.reset();
  }

  // ----- pause refcount (shared game) -----

  private get isPaused(): boolean {
    return this.pauseRefcount > 0 || this.pausedByDevtools;
  }

  // Called by a tenant's pause_rom (via TenantContext). Each tenant's own
  // one-shot guard ensures it contributes at most one to the count.
  private acquirePause(): void {
    const was = this.isPaused;
    this.pauseRefcount++;
    if (!was && this.isPaused) this.pausedBackdrop.capture(this.framebuffer);
  }
  private releasePause(): void {
    const was = this.isPaused;
    if (this.pauseRefcount > 0) this.pauseRefcount--;
    if (was && !this.isPaused) this.pausedBackdrop.clear();
  }

  // ----- frame event fan-out -----

  // Fires inside core.frame() when the game commits sprite state via
  // $4014. Every tenant's event_client_frame runs here so reads see
  // at-DMA RAM; their draw calls are composited in frameTick after
  // core.frame() returns.
  private handleOamDma(): void {
    this.frameGate.fireAtDma(() => this.fireAllTenants());
  }

  private fireAllTenants(): void {
    // Snapshot: a tenant's frame handler may spawn/despawn, mutating the
    // list mid-iteration. A freshly spawned tenant fires next frame; a
    // despawned one is skipped via isStopped().
    for (const t of [...this.tenants]) {
      if (!t.isStopped()) t.fireFrame();
    }
    // Every rider has taken this frame; flush each bus's buffered outbound
    // frames as coalesced envelopes (no-op unless coalescing is on). This is
    // the end-of-frame barrier that makes coalescing add zero latency.
    for (const bus of this.buses.values()) bus.flush();
  }

  // ----- frame loop -----
  //
  // requestAnimationFrame wakes us once per vsync; the wall clock stays the
  // source of truth so emulation holds real time — multiplayer peers each pin
  // to the same real-time frame budget, and high-refresh displays don't run
  // the game fast. Each wake we add the elapsed time as fractional frames
  // "owed" and run ROUND(owed) of them. On a 60 Hz panel owed sits at ~1.0, so
  // we run exactly one frame per refresh: a clean 1:1 cadence that is robust to
  // rAF jitter and coarse (1 ms) timer resolution — no more 0-frame holds /
  // 2-frame drops from sub-frame wobble around a hard threshold. A genuine
  // stall pushes owed past 1.5 and earns a catch-up frame, so real time (and
  // lockstep) is still held; round()'s inherent ±0.5-frame deadband IS the
  // catch-up threshold — jitter stays inside it, real drift crosses it. This
  // self-adapts to any refresh with no rate detection (120 Hz → 1,0,1,0;
  // 144 Hz → the evenest 60-in-144 spread) and degrades to plain catch-up
  // under sustained overload. Capped so a background-tab stall can't
  // fast-forward, and floored so a fast display can't bank unbounded credit.
  private static readonly MAX_CATCHUP_FRAMES = 4;

  private startFrameLoop(): void {
    // Pace to the CORE's native rate, not a flat 60 fps: a 59.7275 fps core
    // (Game Boy) paced at 60 runs ~0.46% fast and overproduces audio, which
    // eventually overflows the output ring and crackles. See
    // BaseCore.frameRateHz.
    const FRAME_MS = 1000 / this.core.frameRateHz;
    const MAX = MunosScriptHost.MAX_CATCHUP_FRAMES;
    let lastNow = -1;
    // Fractional frames of real time owed but not yet run. Hovers near 0 in
    // steady state; a stall bumps it up (earning a catch-up), a fast display
    // pushes it down (earning a hold).
    let owedFrames = 0;
    const tick = (): void => {
      if (this.stopped) return;
      const now = performance.now();
      if (lastNow < 0) lastNow = now;
      // Real time elapsed since the previous wake, converted to fractional
      // frames below (owedFrames clamps it so a stall can't fast-forward).
      const dtMs = now - lastNow;
      lastNow = now;
      owedFrames += dtMs / FRAME_MS;
      // Clamp: a background-tab stall can't fast-forward (high end); a fast
      // display can't bank unbounded "ahead" credit that would later starve
      // the game of frames (low end).
      if (owedFrames > MAX) owedFrames = MAX;
      else if (owedFrames < -1) owedFrames = -1;
      let ran = Math.round(owedFrames);
      if (ran < 0) ran = 0;
      else if (ran > MAX) ran = MAX;
      for (let i = 0; i < ran; i++) {
        try {
          this.frameTick();
        } catch (e) {
          console.error("frameTick threw:", e);
        }
      }
      owedFrames -= ran;
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  // ----- sound: voice a parsed VGM through a synth core (docs/sound.md) -----
  //
  // Audio is a shared-game output, so playback lives on the supervisor, not
  // a tenant. A tenant hands its parsed VGM up via TenantContext.playSound;
  // we bind it to a synth core built per chip on demand and make it the
  // single active voice (last play wins across all tenants). Shared by the
  // tenant path and the devtools VGM playback below.
  playSound(file: VgmFile, loop: boolean): void {
    const res = this.startSynthForFile(file, loop);
    if (!res.ok) console.warn(`munos: play_sound — ${res.error}`);
  }

  // Start a voice for a parsed VGM: render it to PCM (cached after the first
  // time), then add a lightweight buffer-mixing voice. Appends — never
  // replaces — so concurrent sounds overlap. Returns the voice so the
  // devtools playback can track its own.
  private startSynthForFile(
    file: VgmFile, loop: boolean,
  ): { ok: true; voice: PcmVoice } | { ok: false; error: string } {
    const pcm = this.pcmForFile(file);
    if (!pcm) return { ok: false, error: "VGM targets no chip this build can synthesize (or no synth-core factory)" };
    if (pcm.length === 0) return { ok: false, error: "VGM rendered no audio" };
    const voice: PcmVoice = { pcm, pos: 0, loop, finished: false };
    this.activeVoices.push(voice);
    return { ok: true, voice };
  }

  // Synthesize a VGM to an interleaved-stereo PCM buffer, ONCE, and cache it
  // keyed by the parsed file. This is the only place a synth core is stepped
  // for script sound — it runs to the VGM's end up front (a brief one-time
  // cost on first play of each distinct sound), so every later/overlapping
  // play is a free buffer replay. A looping voice replays the whole buffer
  // (correct for the loopFrame=0 captures this build produces; a VGM with a
  // non-zero loop point / intro would need its loop offset honoured here).
  private pcmForFile(file: VgmFile): Float32Array | null {
    const cached = this.pcmCache.get(file);
    if (cached) return cached;
    const chip = file.chips[0]?.chip;
    if (!chip) return null;
    const core = this.acquireSynthCore(chip);
    if (!core) return null;
    // The synth core renders at the sink's actual rate; tell the synth so
    // it converts the VGM's 44.1 kHz wait clock (else playback runs fast).
    const synth = new VgmSynth(file, core, { loop: false }, this.audioSampleRate);
    if (!synth.playable) {
      this.releaseSynthCore(chip, core);
      return null;
    }
    synth.reset();
    const chunks: Float32Array[] = [];
    let total = 0;
    for (let f = 0; f < MAX_RENDER_FRAMES && !synth.finished; f++) {
      const chunk = synth.render(this.synthSamplesPerFrame);
      if (chunk && chunk.length) { chunks.push(chunk); total += chunk.length; }
    }
    this.releaseSynthCore(chip, core);
    const pcm = new Float32Array(total);
    let o = 0;
    for (const c of chunks) { pcm.set(c, o); o += c.length; }
    this.pcmCache.set(file, pcm);
    return pcm;
  }

  // Pull the next `frames` stereo frames from a voice's buffer into a fresh
  // mix chunk, advancing its cursor. A one-shot that reaches the end is
  // marked finished (and its tail zero-filled); a loop wraps to 0.
  private pullVoice(voice: PcmVoice, frames: number): Float32Array {
    const need = frames * 2; // interleaved stereo
    const out = new Float32Array(need);
    let written = 0;
    while (written < need && !voice.finished) {
      if (voice.pos >= voice.pcm.length) {
        if (voice.loop && voice.pcm.length > 0) { voice.pos = 0; }
        else { voice.finished = true; break; }
      }
      const take = Math.min(need - written, voice.pcm.length - voice.pos);
      out.set(voice.pcm.subarray(voice.pos, voice.pos + take), written);
      voice.pos += take;
      written += take;
    }
    return out;
  }

  // Take an idle core for `chip` (reused from the pool) or build a fresh
  // one. Null only when no factory was supplied. Warns once so a sound-
  // using script on a host without audio wiring degrades to silence.
  private acquireSynthCore(chip: ChipId): SynthCore | null {
    const pool = this.idleCores.get(chip);
    const reused = pool?.pop();
    if (reused) return reused;
    if (!this.makeSynthCore) {
      if (!this.warnedNoSynthFactory) {
        this.warnedNoSynthFactory = true;
        console.warn("munos: play_sound called but this host has no synth-core factory; sound disabled");
      }
      return null;
    }
    const core = this.makeSynthCore(chip);
    if (!core || !supportsAudioSynth(core)) {
      console.error(`munos: makeSynthCore('${chip}') did not return an audio-synth-capable core`);
      return null;
    }
    return core;
  }

  // Return a finished voice's core to the pool, silenced so it carries no
  // stale channel state into its next reuse.
  private releaseSynthCore(chip: ChipId, core: SynthCore): void {
    const [reg, val] = SILENCE_WRITE[chip];
    core.writeAudioRegister(reg, val);
    core.drainAudio(); // discard the silence tail
    let pool = this.idleCores.get(chip);
    if (!pool) { pool = []; this.idleCores.set(chip, pool); }
    pool.push(core);
  }

  // ----- devtools sound capture / playback (HostView) -----
  //
  // Capture rips the running GAME's APU writes (a VgmRecorder over the
  // shared core) into a VGM; playback drives a separate synth core, mixed
  // into onAudio by frameTick. The two are independent — you can review a
  // capture while the game is paused. Surfaced through HostView for the
  // devtools panel.

  startSoundCapture(): boolean {
    const core = this.core;
    if (!supportsAudioCapture(core) || !supportsAudioSynth(core)) return false;
    this.soundRecorder = new VgmRecorder(core);
    this.soundRecorder.start();
    this.soundCapturing = true;
    return true;
  }

  stopSoundCapture(): void {
    if (this.soundRecorder) this.soundRecorder.stop();
    this.soundCapturing = false;
  }

  isCapturingSound(): boolean { return this.soundCapturing; }
  soundCaptureFrameCount(): number { return this.soundRecorder?.frameCount ?? 0; }
  soundCaptureWriteCount(): number { return this.soundRecorder?.writeCount ?? 0; }

  // Serialize the current capture to a .vgm. `loop` marks it as a looping
  // music track (from frame 0) vs a one-shot SFX. null when nothing's been
  // captured.
  //
  // Auto-trims silence (docs/sound.md §"Auto-trim"): leading silence is
  // always removed; trailing silence too for a one-shot SFX, but kept for a
  // loop (its final rest may be intentional). The active span is found by
  // replaying the raw capture through a temp synth core and measuring per-
  // frame amplitude — so it degrades gracefully (no synth factory, or audio
  // present every frame ⇒ no trim).
  soundCaptureToVgm(loop: boolean): Uint8Array | null {
    const rec = this.soundRecorder;
    if (!rec || rec.frameCount === 0) return null;
    const loopOpt = loop ? { loopFrame: 0 } : {};
    const raw = rec.toVgm(loopOpt);
    const file = parseVgm(raw);
    const chip = file.chips[0]?.chip;
    if (!chip) return raw;
    const core = this.acquireSynthCore(chip);
    if (!core) return raw; // no synth core to measure with — keep untrimmed
    let span: { first: number; last: number } | null;
    try {
      span = measureActiveFrameSpan(file, core, this.audioSampleRate, rec.frameCount);
    } finally {
      this.releaseSynthCore(chip, core);
    }
    if (!span) return raw; // all silence (or below threshold) — don't trim to nothing
    return rec.toVgm({ ...loopOpt, trimTo: span, trimTrailing: !loop });
  }

  // Play arbitrary VGM bytes (a capture or an imported file) through a
  // synth core, audible over the game. Replaces the previous devtools-
  // initiated voice (the panel reviews one VGM at a time) but leaves
  // script-fired voices untouched.
  playVgmBytes(bytes: Uint8Array, loop: boolean): { ok: true } | { ok: false; error: string } {
    let file: VgmFile;
    try {
      file = parseVgm(bytes);
    } catch (e) {
      return { ok: false, error: `could not parse VGM (${(e as Error).message})` };
    }
    this.stopVgmPlayback();
    const res = this.startSynthForFile(file, loop);
    if (!res.ok) return res;
    this.devtoolsVoice = res.voice;
    return { ok: true };
  }

  stopVgmPlayback(): void {
    if (!this.devtoolsVoice) return;
    this.removeVoice(this.devtoolsVoice);
    this.devtoolsVoice = null;
  }
  isPlayingVgm(): boolean {
    return this.devtoolsVoice !== null && !this.devtoolsVoice.finished;
  }

  // Drop a voice from the mix. PCM voices hold no core, so there's nothing
  // to release — the render pool was returned to right after rendering.
  private removeVoice(voice: PcmVoice): void {
    const i = this.activeVoices.indexOf(voice);
    if (i >= 0) this.activeVoices.splice(i, 1);
  }

  private frameTick(): void {
    this.frameGate.beginTick();
    if (this.isPaused) {
      // Skip core.frame() so the CPU stays halted; restore the pre-pause
      // framebuffer so tenants draw on a stable backdrop.
      this.pausedBackdrop.restore(this.framebuffer);
    } else {
      try {
        this.core.frame();
      } catch (e) {
        console.error("core.frame threw:", e);
        return;
      }
      this.framebuffer.set(this.core.framebuffer);
      // Advance the sound-capture clock once per real emulated frame, so
      // APU writes during this frame are stamped to it (paused frames don't
      // advance — nothing ran).
      if (this.soundCapturing && this.soundRecorder) this.soundRecorder.markFrame();
    }
    // No-DMA-this-tick fallback: paused, or the game skipped its $4014.
    this.frameGate.fireFallback(() => this.fireAllTenants());
    // Composite every tenant's draw calls in load order, then overscan. The
    // background-priority mask (present on the NES core, meaningful while
    // background rendering is on) lets a tenant's behind_background draws —
    // e.g. a pipe-diving ghost — sink behind local level geometry.
    const bgMask = this.core.isBackgroundEnabled() && this.core.readBackgroundMask
      ? this.core.readBackgroundMask()
      : null;
    for (const t of [...this.tenants]) {
      if (!t.isStopped()) t.paintInto(this.framebuffer, this.screenW, this.screenH, bgMask);
    }
    applyOverscanMask(this.framebuffer, this.screenW, this.screenH, this.overscan);
    this.userOnFrame(this.framebuffer);
    if (this.userOnAudio) {
      const gameSamples = this.core.drainAudio();
      // Mix one frame from every active voice — length-matched to the game's
      // own output so they stay aligned — sum them all (game + voices) WITHOUT
      // clamping, then DC-remove + limit the result. The APU streams each carry
      // a large positive DC offset; summing them and clamping at 1.0 is what
      // clipped the top off the waveform, so we recenter (remove DC) and limit.
      //
      // Crucially this runs EVERY frame, voices or not. When the DC removal ran
      // ONLY while a voice was active (the old fast path emitted game audio
      // untouched), the output's DC baseline stepped from +pedestal to ~0 the
      // instant a voice started and back the instant it was reaped — a step in
      // the waveform is an audible *snap*, and its size is the game audio's own
      // DC offset. So it appeared only over loud background music and vanished
      // on a silent screen or under the mute-music mod (both leave the APU near
      // its quiescent DC). Handling DC identically every frame removes the step.
      // Game-only DC removal is inaudible (a constant offset can't be heard),
      // so the normal no-voice path sounds unchanged. (docs/sound.md §4)
      const n = gameSamples ? gameSamples.length >> 1 : this.synthSamplesPerFrame;
      let out = gameSamples;
      for (const voice of this.activeVoices) {
        out = addAudio(out, this.pullVoice(voice, n));
      }
      if (out) {
        removeDcAndLimit(out);
        this.userOnAudio(out);
      }
      // Reap one-shot voices that reached the end of their buffer.
      for (let i = this.activeVoices.length - 1; i >= 0; i--) {
        const voice = this.activeVoices[i]!;
        if (voice.finished) {
          this.activeVoices.splice(i, 1);
          if (voice === this.devtoolsVoice) this.devtoolsVoice = null;
        }
      }
    }
    // Battery save cadence: re-key on a bundle change (hop/spawn) and
    // dirty-flush, every BATTERY_FLUSH_FRAMES so the getBattery() copy + hash
    // isn't taken every frame. Fire-and-forget — the async re-key self-guards.
    if (this.batterySave && ++this.batteryFlushCounter >= MunosScriptHost.BATTERY_FLUSH_FRAMES) {
      this.batteryFlushCounter = 0;
      void this.tickBattery();
    }
  }

  // ----- input fan-out -----
  //
  // Player-1 edges fan out to EVERY tenant's input latch (non-exclusive
  // reads — no mod can hide a button from a sibling) and the ROM sees the
  // edge only if NO tenant consumed it. Player-2 bypasses the Munos
  // surface (v1 is single-controller per docs/language.md).

  buttonDown(button: number, player = 1): void {
    const c = (player === 2 ? 2 : 1);
    if (player !== 1) {
      this.core.buttonDown(c, button);
      return;
    }
    let consumed = false;
    for (const t of [...this.tenants]) {
      if (!t.isStopped() && t.pressButton(button)) consumed = true;
    }
    if (!consumed) this.core.buttonDown(c, button);
  }
  buttonUp(button: number, player = 1): void {
    const c = (player === 2 ? 2 : 1);
    if (player !== 1) {
      this.core.buttonUp(c, button);
      return;
    }
    let consumed = false;
    for (const t of [...this.tenants]) {
      if (!t.isStopped() && t.releaseButton(button)) consumed = true;
    }
    if (!consumed) this.core.buttonUp(c, button);
  }

  // ----- HostView: shared-game introspection (delegates to core) -----

  readOam(): Uint8Array { return this.core.readOam(); }
  isSpritesEnabled(): boolean { return this.core.isSpritesEnabled(); }
  isBackgroundEnabled(): boolean { return this.core.isBackgroundEnabled(); }
  readRamByte(addr: number): number { return this.core.readCpuByte(addr); }
  writeRamByte(addr: number, value: number): void { this.core.writeCpuByte(addr, value); }
  readRamSnapshot(): Uint8Array { return this.core.readRamSnapshot(); }
  readSpriteHeight(): 8 | 16 { return this.core.readSpriteHeight(); }
  readSpritePalette(): Uint32Array { return this.core.readSpritePalette(); }
  readTilePixels(globalIndex: number): Uint8Array { return this.core.readTilePixels(globalIndex); }
  readBackgroundPalette(): Uint32Array { return this.core.readBackgroundPalette(); }

  // NES-only introspection — narrow the core union or throw with a clear
  // message so a stray call surfaces rather than crashing inside a wrapper.
  private nesCore(): NesEmulatorCore {
    if (this.core.system !== "nes") {
      throw new Error(`MunosScriptHost: NES-PPU introspection is not available for system '${this.core.system}'`);
    }
    return this.core;
  }
  private gbCore(): GbEmulatorCore {
    if (this.core.system !== "gb") {
      throw new Error(`MunosScriptHost: GB-specific introspection is not available for system '${this.core.system}'`);
    }
    return this.core;
  }

  // Raw per-slot color-identity tokens — NES: 16 master-palette indices
  // (0..63); GB: 8 DMG shade tokens (0..3). Both cores expose this through
  // their per-system extension, so the union dispatches correctly.
  readSpritePaletteIndices(): Uint8Array { return this.core.readSpritePaletteIndices(); }
  readSpritePatternTableBase(): 0 | 1 { return this.nesCore().readSpritePatternTableBase(); }
  readTilePixelsAtScanline(globalIndex: number, scanline: number): Uint8Array {
    return this.nesCore().readTilePixelsAtScanline(globalIndex, scanline);
  }
  readBackgroundPatternTableBase(): 0 | 1 { return this.nesCore().readBackgroundPatternTableBase(); }
  readBackgroundTilemap(): { tiles: Uint8Array; palettes: Uint8Array } {
    return this.nesCore().readBackgroundTilemap();
  }
  readGbBackgroundTilemap(): GbBackgroundTilemap {
    return this.gbCore().readBackgroundTilemap();
  }

  // ----- HostView: transport controls (play / pause / step / restart) -----

  pauseRom(): void {
    const was = this.isPaused;
    this.pausedByDevtools = true;
    if (!was && this.isPaused) this.pausedBackdrop.capture(this.framebuffer);
  }
  resumeRom(): void {
    const was = this.isPaused;
    this.pausedByDevtools = false;
    // A tenant may still hold a pause (any-pauses-wins) — only clear the
    // backdrop if the game is actually about to run again.
    if (was && !this.isPaused) this.pausedBackdrop.clear();
  }
  isRomPaused(): boolean { return this.isPaused; }

  // Advance one frame while paused. Mirrors one iteration of frameTick's
  // running path so the stepped frame behaves like a real frame: arm the
  // frame-event gate, run the core, then deliver the tenants' per-frame
  // events. Without beginTick() the gate is left un-armed (the prior tick's
  // fireFallback cleared `pending`), so the OAM-DMA hook would suppress
  // fireAtDma and the stepped frame would silently skip every tenant's
  // event_client_frame. Backdrop is captured clean (no tenant paint) — the
  // ongoing paused frameTick loop composites overlays on top each frame.
  stepFrame(): void {
    if (!this.isPaused) return;
    this.frameGate.beginTick();
    try {
      this.core.frame();
    } catch (e) {
      console.error("core.frame threw during stepFrame:", e);
      return;
    }
    this.framebuffer.set(this.core.framebuffer);
    // A real emulated frame ran — advance the sound-capture clock so APU
    // writes during it are stamped to the stepped frame (matches frameTick).
    if (this.soundCapturing && this.soundRecorder) this.soundRecorder.markFrame();
    // fireAtDma already fired the tenants if the game ran its OAM DMA;
    // otherwise dispatch here so the script still gets its per-frame call.
    this.frameGate.fireFallback(() => this.fireAllTenants());
    this.pausedBackdrop.capture(this.framebuffer);
  }

  // Soft-reset: re-call loadROM (rebuilds the mapper the way start() does),
  // re-install the OAM-DMA hook, and fully unpause.
  resetRom(): void {
    this.core.loadROM(this.rom);
    this.core.onOamDmaWrite(() => this.handleOamDma());
    // Clear each tenant's one-shot pause guard so it agrees with the forced
    // unpause below. Zeroing pauseRefcount alone desyncs: a tenant that held
    // pause_rom keeps its internal selfPauseRequested guard set, so its NEXT
    // pause_rom is a silent no-op (the game won't re-pause) while its next
    // resume can't restore the count — after a devtools reset the shared game
    // can get stuck. resetPause() releases each holder's contribution and
    // clears its guard; the count then lands back at 0 on its own (the
    // assignment below is a defensive floor, not the mechanism).
    for (const t of this.tenants) t.resetPause();
    this.pauseRefcount = 0;
    this.pausedByDevtools = false;
    this.pausedBackdrop.clear();
  }

  // ----- HostView: savestate (core-private envelope) -----

  snapshot(): unknown { return this.core.saveState(); }
  restore(state: unknown): void { this.core.loadState(coerceLegacySaveState(state)); }

  // ----- HostView: script-overlay introspection (authored tenant) -----

}
